import { create } from 'zustand';

interface MaintenanceState {
    /** API is returning 503 with MAINTENANCE envelope OR Caddy fallback */
    isMaintenance: boolean;
    /** Seconds hint from Retry-After / retry_after_seconds */
    retryAfterSeconds: number;
    /** Epoch ms when maintenance was first detected */
    detectedAt: number | null;
    /** Last successful non-maintenance response — used to auto-clear the banner */
    lastSuccessAt: number | null;
    /** Signal maintenance from an axios interceptor. */
    notifyMaintenance: (retryAfterSeconds: number) => void;
    /** Signal a successful response — clears maintenance flag. */
    notifySuccess: () => void;
    /** Manual reset (e.g. after user retries from banner). */
    reset: () => void;
}

export const useMaintenanceStore = create<MaintenanceState>((set, get) => ({
    isMaintenance: false,
    retryAfterSeconds: 30,
    detectedAt: null,
    lastSuccessAt: null,
    notifyMaintenance: (retryAfterSeconds: number) => {
        const current = get();
        set({
            isMaintenance: true,
            retryAfterSeconds: Math.max(5, Math.min(300, retryAfterSeconds || 30)),
            detectedAt: current.detectedAt ?? Date.now(),
        });
    },
    notifySuccess: () => {
        // Only set state when we are actually recovering from a maintenance
        // window. In steady state, we avoid re-rendering every subscriber on
        // each successful HTTP response.
        const current = get();
        if (current.isMaintenance) {
            set({ isMaintenance: false, detectedAt: null, lastSuccessAt: Date.now() });
        }
    },
    reset: () => set({ isMaintenance: false, detectedAt: null, retryAfterSeconds: 30 }),
}));
